import React, { useEffect, useState } from 'react';
import { Calendar, CheckCircle2, Loader2, MapPin, MessageCircle, RotateCcw, Unlink, Video, XCircle } from 'lucide-react';
import { AllodosLogo } from './AllodosLogo';
import { appointmentActionsApi, type AppointmentAction as Action, type AppointmentActionView } from '../services/appointmentActionsApi';
import { describeError } from '../utils/describeError';

const REASONS = ['Imprevisto pessoal', 'Problema de saúde', 'Conflito de horário', 'Outro motivo'];

const longDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  const s = new Date(y, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const isOnline = (v: AppointmentActionView) => /tele|online|remot/i.test(v.modality ?? '');

/**
 * Página pública dos links do WhatsApp (Confirmar / Cancelar / Reagendar).
 * Confirmar e cancelar exigem um toque: a prévia de link do WhatsApp (e
 * antivírus que abrem links) não podem responder pelo paciente.
 */
export const AppointmentAction: React.FC<{ token: string; action: Action }> = ({ token, action }) => {
  const [view, setView] = useState<AppointmentActionView | null>(null);
  const [mode, setMode] = useState<Action>(action);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [done, setDone] = useState<'CONFIRMADO' | 'CANCELADO' | null>(null);

  useEffect(() => {
    appointmentActionsApi
      .view(token)
      .then((v) => {
        setView(v);
        // Abrir "Reagendar" já registra a intenção; o novo horário é escolhido na página de agendamento.
        if (action === 'reagendar' && v.canAct) void appointmentActionsApi.reschedule(token).catch(() => undefined);
      })
      .catch((err) => setError({ status: (err as { status?: number }).status, message: describeError(err) }));
  }, [token, action]);

  const run = async (fn: () => Promise<AppointmentActionView>, result: 'CONFIRMADO' | 'CANCELADO') => {
    setBusy(true);
    try {
      setView(await fn());
      setDone(result);
    } catch (err) {
      setError({ status: (err as { status?: number }).status, message: describeError(err) });
    } finally {
      setBusy(false);
    }
  };

  const invalid = error?.status === 404 || error?.status === 410 || (view && view.expired);

  const Summary: React.FC<{ strike?: boolean }> = ({ strike }) =>
    view ? (
      <div className="rounded-3xl bg-slate-50 border border-slate-100 overflow-hidden">
        <div className="p-4 flex items-center gap-3 border-b border-slate-100">
          <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-sm font-black">
            {(view.professionalName ?? 'P').split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')}
          </div>
          <div className="min-w-0">
            <p className="text-[15px] font-black text-slate-800 truncate">{view.professionalName ?? 'Profissional'}</p>
            <p className="text-xs font-semibold text-slate-500 truncate">{[view.professionalCrp, view.clinicName].filter(Boolean).join(' · ')}</p>
          </div>
        </div>
        <div className={`p-4 flex gap-3 border-b border-slate-100 ${strike ? 'line-through' : ''}`}>
          <Calendar size={18} className="text-indigo-600 shrink-0" />
          <div>
            <p className="text-sm font-extrabold text-slate-800">{longDate(view.date)}</p>
            <p className="text-xs font-semibold text-slate-500">{view.start} às {view.end}</p>
          </div>
        </div>
        <div className="p-4 flex gap-3">
          {isOnline(view) ? <Video size={18} className="text-indigo-600 shrink-0" /> : <MapPin size={18} className="text-indigo-600 shrink-0" />}
          <div>
            <p className="text-sm font-extrabold text-slate-800">{isOnline(view) ? 'Teleconsulta' : view.locationName || 'Presencial'}</p>
            {isOnline(view) && <p className="text-xs font-semibold text-slate-500">O link da sala está na mensagem do WhatsApp.</p>}
          </div>
        </div>
      </div>
    ) : null;

  const bigBtn = 'w-full flex items-center justify-center gap-2.5 p-[18px] rounded-[1.4rem] text-xs font-black uppercase tracking-[0.14em] disabled:opacity-60';
  const linkBtn = 'flex-1 text-center p-3 text-[13px] font-extrabold';

  let body: React.ReactNode;
  if (error && !invalid) {
    body = <p role="alert" className="p-4 rounded-2xl bg-rose-50 border border-rose-100 text-sm font-bold text-rose-700">{error.message}</p>;
  } else if (invalid) {
    body = (
      <>
        <div className="w-[72px] h-[72px] rounded-3xl bg-slate-100 text-slate-500 flex items-center justify-center"><Unlink size={34} /></div>
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Este link não vale mais</h1>
          <p className="mt-1.5 text-sm font-medium text-slate-600 leading-relaxed">A consulta já começou, já passou ou o link não existe. Para qualquer alteração, fale com a clínica.</p>
        </div>
        {view?.clinicPhone && (
          <a href={`https://wa.me/${view.clinicPhone.replace(/\D/g, '').replace(/^(?!55)(\d{10,11})$/, '55$1')}`} className={`${bigBtn} bg-indigo-600 text-white`}>
            <MessageCircle size={16} /> Falar com a clínica
          </a>
        )}
      </>
    );
  } else if (!view) {
    body = <div className="py-10 sm:py-16 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={22} /></div>;
  } else if (view.status === 'CANCELADO') {
    body = (
      <>
        <div className="w-[72px] h-[72px] rounded-3xl bg-rose-50 text-rose-700 flex items-center justify-center"><XCircle size={34} /></div>
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Consulta cancelada</h1>
          <p className="mt-1.5 text-sm font-medium text-slate-600 leading-relaxed">{done ? 'Avisamos a clínica e o horário foi liberado.' : 'Esta consulta já estava cancelada.'}</p>
        </div>
        <Summary strike />
        {view.rescheduleUrl && <a href={view.rescheduleUrl} className={`${bigBtn} bg-indigo-600 text-white`}><Calendar size={16} /> Agendar novo horário</a>}
      </>
    );
  } else if (mode === 'reagendar') {
    body = (
      <>
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Reagendar consulta</h1>
          <p className="mt-1.5 text-sm font-medium text-slate-600 leading-relaxed">
            Escolha um novo horário na página de agendamento. Depois de marcar o novo, cancele este horário para liberá-lo.
          </p>
        </div>
        <Summary />
        {view.rescheduleUrl ? (
          <a href={view.rescheduleUrl} className={`${bigBtn} bg-indigo-600 text-white`}><RotateCcw size={16} /> Escolher novo horário</a>
        ) : (
          <p className="p-4 rounded-2xl bg-amber-50 border border-amber-100 text-sm font-bold text-amber-900">A clínica não tem agendamento online ativo. Fale com a clínica para remarcar.</p>
        )}
        <div className="flex gap-2">
          <button onClick={() => setMode('cancelar')} className={`${linkBtn} text-rose-700`}>Cancelar este horário</button>
          <button onClick={() => setMode('confirmar')} className={`${linkBtn} text-indigo-600`}>Manter e confirmar</button>
        </div>
      </>
    );
  } else if (mode === 'cancelar') {
    body = (
      <>
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Cancelar esta consulta?</h1>
          <p className="mt-1.5 text-sm font-medium text-slate-600 leading-relaxed">O horário será liberado para outra pessoa. Você pode agendar outro depois.</p>
        </div>
        <Summary />
        <fieldset className="space-y-2">
          <legend className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Motivo (opcional)</legend>
          {REASONS.map((r) => (
            <label key={r} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-sm font-bold text-slate-700 ${reason === r ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200'}`}>
              <input type="radio" name="motivo" checked={reason === r} onChange={() => setReason(r)} className="accent-indigo-600" />
              {r}
            </label>
          ))}
        </fieldset>
        <button onClick={() => run(() => appointmentActionsApi.cancel(token, reason), 'CANCELADO')} disabled={busy} className={`${bigBtn} bg-rose-700 text-white`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />} Sim, cancelar consulta
        </button>
        <button onClick={() => setMode('reagendar')} className={`${linkBtn} text-indigo-600`}>Prefiro reagendar</button>
      </>
    );
  } else if (view.status === 'CONFIRMADO' || done === 'CONFIRMADO') {
    body = (
      <>
        <div className="w-[72px] h-[72px] rounded-3xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><CheckCircle2 size={34} /></div>
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Presença confirmada!</h1>
          <p className="mt-1.5 text-sm font-medium text-slate-600 leading-relaxed">A clínica já foi avisada. Até lá!</p>
        </div>
        <Summary />
        <div className="flex gap-2">
          <button onClick={() => setMode('cancelar')} className={`${linkBtn} text-rose-700`}>Cancelar</button>
          <button onClick={() => setMode('reagendar')} className={`${linkBtn} text-indigo-600`}>Reagendar</button>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Confirmar sua consulta?</h1>
          <p className="mt-1.5 text-sm font-medium text-slate-600 leading-relaxed">Toque no botão para avisar que você vai comparecer.</p>
        </div>
        <Summary />
        <button onClick={() => run(() => appointmentActionsApi.confirm(token), 'CONFIRMADO')} disabled={busy} className={`${bigBtn} bg-emerald-600 text-white`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Confirmar presença
        </button>
        <div className="flex gap-2">
          <button onClick={() => setMode('cancelar')} className={`${linkBtn} text-rose-700`}>Preciso cancelar</button>
          <button onClick={() => setMode('reagendar')} className={`${linkBtn} text-indigo-600`}>Quero reagendar</button>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 flex flex-col items-center px-4 py-6 sm:py-14 gap-5 overflow-y-auto">
      <div className="w-full max-w-[480px] flex items-center justify-between">
        <AllodosLogo className="w-[104px] h-auto" />
        {view?.clinicName && <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 truncate ml-3">{view.clinicName}</span>}
      </div>
      <main className="w-full max-w-[480px] bg-white border border-slate-200 rounded-[2.5rem] shadow-[0_20px_25px_-5px_rgba(15,23,42,0.06)] px-5 sm:px-7 py-7 flex flex-col gap-[18px]">
        {body}
      </main>
      <p className="w-full max-w-[480px] text-center text-[11px] font-semibold text-slate-500">Link pessoal. Não encaminhe esta página.</p>
    </div>
  );
};

export default AppointmentAction;
